export function formatDate(
  date: string | Date,
  locale = 'en',
  options: Intl.DateTimeFormatOptions = {},
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  };

  try {
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch (error) {
    // Fallback to English if locale is not supported
    return new Intl.DateTimeFormat('en', defaultOptions).format(dateObj);
  }
}

export function formatRelativeDate(date: string | Date, locale = 'en'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    if (diffInDays > 0) {
      return rtf.format(-diffInDays, 'day');
    } else if (diffInHours > 0) {
      return rtf.format(-diffInHours, 'hour');
    } else if (diffInMinutes > 0) {
      return rtf.format(-diffInMinutes, 'minute');
    } else {
      return rtf.format(0, 'second');
    }
  } catch (error) {
    // Fallback to English if locale is not supported
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    if (diffInDays > 0) {
      return rtf.format(-diffInDays, 'day');
    } else if (diffInHours > 0) {
      return rtf.format(-diffInHours, 'hour');
    } else if (diffInMinutes > 0) {
      return rtf.format(-diffInMinutes, 'minute');
    } else {
      return rtf.format(0, 'second');
    }
  }
}

export function formatDateTime(
  date: string | Date,
  locale = 'en',
  options: Intl.DateTimeFormatOptions = {},
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };

  return formatDate(date, locale, defaultOptions);
}

export function formatTime(
  date: string | Date,
  locale = 'en',
  options: Intl.DateTimeFormatOptions = {},
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };

  return formatDate(date, locale, defaultOptions);
}

export function isToday(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();

  return (
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear()
  );
}

export function isTomorrow(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    dateObj.getDate() === tomorrow.getDate() &&
    dateObj.getMonth() === tomorrow.getMonth() &&
    dateObj.getFullYear() === tomorrow.getFullYear()
  );
}

export function isYesterday(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    dateObj.getDate() === yesterday.getDate() &&
    dateObj.getMonth() === yesterday.getMonth() &&
    dateObj.getFullYear() === yesterday.getFullYear()
  );
}

export function isThisWeek(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return dateObj >= startOfWeek && dateObj <= endOfWeek;
}

export function getDaysDifference(date1: string | Date, date2: string | Date): number {
  const dateObj1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' ? new Date(date2) : date2;

  const diffInMs = Math.abs(dateObj2.getTime() - dateObj1.getTime());
  return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
}

export function addDays(date: string | Date, days: number): Date {
  const dateObj = new Date(date);
  dateObj.setDate(dateObj.getDate() + days);
  return dateObj;
}

export function subtractDays(date: string | Date, days: number): Date {
  return addDays(date, -days);
}
